import { Suspense, useDeferredValue } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useUser } from "@/lib/Auth/useUser";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { salesCommissionQueryGraphql } from "@/app/Reports/Reports/GraphQL/reportSalesQuery.graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Currency } from "@/gql/graphql";
import { QuarterSelector } from "@/app/Reports/Reports/Components/QuarterSelector";
import { SalesUserSelector } from "@/app/Reports/Reports/Components/SalesUserSelector";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const ADMIN_EMAILS = ["carl@imoova.com", "william@imoova.com"];

function getQuarterDateRange(quarter: number, year: number): string {
  // Australian FY: Q1=Jul-Sep, Q2=Oct-Dec, Q3=Jan-Mar, Q4=Apr-Jun
  const quarterMonths: Record<number, [number, number]> = {
    1: [7, year - 1],
    2: [10, year - 1],
    3: [1, year],
    4: [4, year],
  };

  const [startMonth, calendarYear] = quarterMonths[quarter];
  const start = new Date(calendarYear, startMonth - 1, 1);
  const end = new Date(calendarYear, startMonth + 2, 0);

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return `${formatDate(start)} – ${formatDate(end)}`;
}

export function ReportSalesScreen() {
  const { quarter, year, userId } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/sales",
  });
  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/sales",
  });
  const user = useUser();

  const email = user?.email ?? "";
  const isCommissionAdmin = ADMIN_EMAILS.includes(email);

  const deferredQuarter = useDeferredValue(quarter);
  const deferredYear = useDeferredValue(year);
  const deferredUserId = useDeferredValue(userId);
  const isPending =
    deferredQuarter !== quarter ||
    deferredYear !== year ||
    deferredUserId !== userId;

  function handleQuarterChange(newQuarter: number, newYear: number) {
    navigate({
      params: (prev) => prev,
      search: { quarter: newQuarter, year: newYear, userId },
    });
  }

  function handleUserChange(newUserId: string | undefined) {
    navigate({
      params: (prev) => prev,
      search: { quarter, year, userId: newUserId },
    });
  }

  return (
    <div className="h-full space-y-6 overflow-y-auto p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Commission</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardHeader>
          <CardTitle>Sales Commission Report</CardTitle>
          <CardDescription>
            {getQuarterDateRange(quarter, year)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <QuarterSelector
              quarter={quarter}
              year={year}
              onQuarterChange={handleQuarterChange}
            />
            {isCommissionAdmin ? (
              <SalesUserSelector
                userId={userId}
                onUserChange={handleUserChange}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Suspense fallback={<Skeleton className="h-96 w-full" />}>
        <CommissionContent
          quarter={deferredQuarter}
          year={deferredYear}
          userId={deferredUserId}
          isPending={isPending}
        />
      </Suspense>
    </div>
  );
}

function CommissionContent({
  quarter,
  year,
  userId,
  isPending,
}: {
  quarter: number;
  year: number;
  userId: string | undefined;
  isPending: boolean;
}) {
  const { data } = useSuspenseGqlQuery(salesCommissionQueryGraphql, {
    quarter,
    year,
    userId: userId ?? null,
  });

  const report = data.salesCommissionReport;

  if (!report) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          No commission data available for this period.
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className="space-y-6 transition-opacity duration-200"
      style={{ opacity: isPending ? 0.5 : 1 }}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Commission</CardDescription>
            <CardTitle className="text-3xl">
              {formatCurrency(report.commissionAmount, Currency.Aud, {
                showZero: true,
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-xs">
              5% of{" "}
              {formatCurrency(report.totalRevenue, Currency.Aud, {
                showZero: true,
              })}{" "}
              revenue
            </p>
            {report.trend?.percentageChange != null ? (
              <div className="mt-2 flex items-center gap-1">
                {report.trend.percentageChange >= 0 ? (
                  <span className="text-green-600">▲</span>
                ) : (
                  <span className="text-red-600">▼</span>
                )}
                <span
                  className={`text-sm font-medium ${
                    report.trend.percentageChange >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {Math.abs(report.trend.percentageChange).toFixed(1)}%
                </span>
                <span className="text-muted-foreground text-xs">
                  vs FY{String(report.year - 2).slice(-2)}/
                  {String(report.year - 1).slice(-2)}
                  {report.trend.isPartialComparison ? " (to date)" : ""}
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Revenue</CardDescription>
            <CardTitle className="text-3xl">
              {formatCurrency(report.totalRevenue, Currency.Aud, {
                showZero: true,
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-xs">
              From {report.bookingCount} booking
              {report.bookingCount !== 1 ? "s" : ""}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Sales Agent</CardDescription>
            <CardTitle className="text-xl">
              {report.user?.name ?? "--"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-xs">
              Q{report.quarter} FY{String(report.year - 1).slice(-2)}/
              {String(report.year).slice(-2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {report.organisations.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Breakdown by Organisation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Organisation</th>
                    <th className="pb-3 text-right font-medium">Bookings</th>
                    <th className="pb-3 text-right font-medium">Revenue</th>
                    <th className="pb-3 text-right font-medium">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {report.organisations.map((org) => (
                    <tr key={org.organisation.id} className="border-b">
                      <td className="py-3 font-medium">
                        {org.organisation.name}
                      </td>
                      <td className="py-3 text-right">{org.bookingCount}</td>
                      <td className="py-3 text-right">
                        {formatCurrency(org.revenue, Currency.Aud, {
                          showZero: true,
                        })}
                      </td>
                      <td className="py-3 text-right font-medium text-green-600">
                        {formatCurrency(org.commissionAmount, Currency.Aud, {
                          showZero: true,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className="pt-3">Total</td>
                    <td className="pt-3 text-right">{report.bookingCount}</td>
                    <td className="pt-3 text-right">
                      {formatCurrency(report.totalRevenue, Currency.Aud, {
                        showZero: true,
                      })}
                    </td>
                    <td className="pt-3 text-right text-green-600">
                      {formatCurrency(report.commissionAmount, Currency.Aud, {
                        showZero: true,
                      })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            No bookings found for this quarter.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
